import useThemeColors from '@/hooks/useThemeColors';
import { useForm } from '@inertiajs/react';
import { MessageSquare, X } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BetaFeedbackWidgetProps {
    alignment?: 'right' | 'left';
}

const BetaFeedbackWidget: React.FC<BetaFeedbackWidgetProps> = ({
    alignment = 'right',
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { data, setData, post, processing, reset } = useForm({
        report_type: '',
        description: '',
        page_url: '',
    });

    const handleOpen = () => {
        setIsOpen(true);
        setIsSubmitted(false);
    };

    const handleClose = () => {
        setIsOpen(false);
        setIsSubmitted(false);
        reset();
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        setData('page_url', window.location.href);

        post('/beta-feedback/', {
            preserveScroll: true,
            onSuccess: () => {
                setIsSubmitted(true);
            },
        });
    };

    return (
        <div
            className={`fixed bottom-6 z-50 flex flex-col gap-4 ${alignment === 'left' ? 'left-6 items-start' : 'right-6 items-end'}`}
        >
            {isOpen && (
                <div className="w-80 max-w-[90vw] rounded-2xl border border-gray-200 bg-white p-5 text-gray-900 shadow-xl">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-sm font-semibold text-gray-900">
                                {t('betaFeedback.title')}
                            </p>
                            <p className="mt-2 text-sm text-gray-600">
                                {t('betaFeedback.subtitle')}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            aria-label={t('betaFeedback.close')}
                        >
                            <X size={16} />
                        </button>
                    </div>

                    {isSubmitted ? (
                        <div className="mt-5 space-y-4">
                            <div className="rounded-xl bg-green-50 px-4 py-3 text-sm text-green-700">
                                <p className="font-semibold">
                                    {t('betaFeedback.successTitle')}
                                </p>
                                <p className="mt-1">
                                    {t('betaFeedback.successMessage')}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleClose}
                                className="w-full rounded-full bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800"
                            >
                                {t('betaFeedback.closeButton')}
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            className="mt-5 space-y-4"
                        >
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">
                                    {t('betaFeedback.reportLabel')}
                                </label>
                                <select
                                    value={data.report_type}
                                    onChange={(event) =>
                                        setData(
                                            'report_type',
                                            event.target.value
                                        )
                                    }
                                    className={`w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:ring-2 focus:outline-none ${colors.inputFocus}`}
                                    required
                                >
                                    <option value="" disabled>
                                        {t('betaFeedback.reportPlaceholder')}
                                    </option>
                                    <option value="problem">
                                        {t('betaFeedback.options.problem')}
                                    </option>
                                    <option value="improvement">
                                        {t('betaFeedback.options.improvement')}
                                    </option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700">
                                    {t('betaFeedback.descriptionLabel')}
                                </label>
                                <textarea
                                    value={data.description}
                                    onChange={(event) =>
                                        setData(
                                            'description',
                                            event.target.value
                                        )
                                    }
                                    rows={4}
                                    className={`w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:ring-2 focus:outline-none ${colors.inputFocus}`}
                                    placeholder={t(
                                        'betaFeedback.descriptionPlaceholder'
                                    )}
                                    required
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={processing}
                                className="btn-gradient w-full rounded-full px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {processing
                                    ? t('betaFeedback.sending')
                                    : t('betaFeedback.submit')}
                            </button>
                        </form>
                    )}
                </div>
            )}

            {!isOpen && (
                <button
                    type="button"
                    onClick={handleOpen}
                    className="btn-gradient flex items-center gap-2 rounded-full px-4 py-3 text-sm font-semibold"
                    aria-label={t('betaFeedback.openButton')}
                >
                    <MessageSquare size={18} />
                    <span>{t('betaFeedback.openButton')}</span>
                </button>
            )}
        </div>
    );
};

export default BetaFeedbackWidget;
